import React from 'react';
import SEO from './SEO';
import '../styles/LegalPages.css';

function TermsOfService() {
  return (
    <div className="legal-page">
      <SEO 
        title="Terms of Service - Clarifyall"
        description="Read Clarifyall's Terms of Service to understand the rules and regulations governing the use of our AI tool directory platform."
        canonicalUrl="/terms"
      />
      
      <div className="legal-container">
        <div className="legal-header">
          <h1>Terms of Service</h1>
          <p className="legal-updated">Last Updated: January 2024</p>
        </div>

        <div className="legal-content">
          <section className="legal-section">
            <h2>1. Acceptance of Terms</h2>
            <p>
              Welcome to Clarifyall. By accessing or using our website and services, you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, please do not use our services.
            </p>
            <p>
              These Terms constitute a legally binding agreement between you and Clarifyall. We reserve the right to modify these Terms at any time, and your continued use of our services after such modifications constitutes your acceptance of the updated Terms.
            </p>
          </section>

          <section className="legal-section">
            <h2>2. Description of Service</h2>
            <p>
              Clarifyall is an AI tool directory platform that provides:
            </p>
            <ul>
              <li>A comprehensive database of AI tools and software</li>
              <li>Search and filtering capabilities to discover AI tools</li>
              <li>User reviews, ratings, and recommendations</li>
              <li>Tool submission and listing services</li>
              <li>Educational content about AI tools and technologies</li>
              <li>User accounts for personalized experiences</li>
            </ul>
            <p>
              We reserve the right to modify, suspend, or discontinue any aspect of our services at any time without prior notice.
            </p>
          </section>

          <section className="legal-section">
            <h2>3. User Accounts</h2>
            
            <h3>3.1 Account Creation</h3>
            <p>To access certain features, you must create an account. You agree to:</p>
            <ul>
              <li>Provide accurate, current, and complete information</li>
              <li>Maintain and update your information to keep it accurate</li>
              <li>Maintain the security of your password</li>
              <li>Accept responsibility for all activities under your account</li>
              <li>Notify us immediately of any unauthorized use</li>
            </ul>

            <h3>3.2 Account Eligibility</h3>
            <p>You must be at least 13 years old to create an account. If you are under 18, you must have parental or guardian consent.</p>

            <h3>3.3 Account Termination</h3>
            <p>We reserve the right to suspend or terminate your account if you violate these Terms or engage in fraudulent, abusive, or illegal activities.</p>
          </section>

          <section className="legal-section">
            <h2>4. User Conduct and Responsibilities</h2>
            <p>You agree NOT to:</p>
            <ul>
              <li>Violate any applicable laws or regulations</li>
              <li>Infringe on intellectual property rights of others</li>
              <li>Submit false, misleading, or fraudulent information</li>
              <li>Engage in spamming, phishing, or other malicious activities</li>
              <li>Attempt to gain unauthorized access to our systems</li>
              <li>Use automated tools (bots, scrapers) without permission</li>
              <li>Harass, abuse, or harm other users</li>
              <li>Upload viruses, malware, or harmful code</li>
              <li>Impersonate others or misrepresent your affiliation</li>
              <li>Interfere with the proper functioning of our services</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>5. Content Submission and Ownership</h2>
            
            <h3>5.1 User-Generated Content</h3>
            <p>
              When you submit content (tool listings, reviews, comments, etc.), you grant Clarifyall a worldwide, non-exclusive, royalty-free license to use, reproduce, modify, publish, and distribute your content in connection with our services.
            </p>

            <h3>5.2 Content Standards</h3>
            <p>All submitted content must:</p>
            <ul>
              <li>Be accurate and truthful</li>
              <li>Not violate any laws or third-party rights</li>
              <li>Not contain offensive, defamatory, or inappropriate material</li>
              <li>Not include spam, advertising, or promotional content (unless authorized)</li>
              <li>Comply with our content guidelines and community standards</li>
            </ul>

            <h3>5.3 Content Moderation</h3>
            <p>
              We reserve the right to review, edit, or remove any content that violates these Terms or our community guidelines. However, we are not obligated to monitor all user-generated content.
            </p>

            <h3>5.4 Ownership</h3>
            <p>
              You retain ownership of your original content. Clarifyall owns all rights to the platform, design, features, and proprietary content.
            </p>
          </section>

          <section className="legal-section">
            <h2>6. Tool Listings and Information</h2>
            
            <h3>6.1 Accuracy of Information</h3>
            <p>
              While we strive to provide accurate information about AI tools, we do not guarantee the completeness, accuracy, or reliability of any tool listings, descriptions, or reviews. Tool information is provided "as is" without warranties.
            </p>

            <h3>6.2 Third-Party Tools</h3>
            <p>
              Clarifyall is a directory platform. We do not develop, own, or operate the AI tools listed on our platform. Your use of third-party AI tools is subject to their respective terms and conditions.
            </p>

            <h3>6.3 No Endorsement</h3>
            <p>
              Listing a tool on Clarifyall does not constitute an endorsement or recommendation. We are not responsible for the performance, quality, or legality of any third-party tools.
            </p>
          </section>

          <section className="legal-section">
            <h2>7. Intellectual Property Rights</h2>
            
            <h3>7.1 Our Intellectual Property</h3>
            <p>
              All content, features, and functionality of Clarifyall, including but not limited to text, graphics, logos, icons, images, audio clips, digital downloads, data compilations, and software, are the exclusive property of Clarifyall and are protected by copyright, trademark, and other intellectual property laws.
            </p>

            <h3>7.2 Limited License</h3>
            <p>
              We grant you a limited, non-exclusive, non-transferable license to access and use our services for personal, non-commercial purposes. You may not:
            </p>
            <ul>
              <li>Copy, modify, or create derivative works</li>
              <li>Reverse engineer or decompile our software</li>
              <li>Remove copyright or proprietary notices</li>
              <li>Use our content for commercial purposes without permission</li>
            </ul>

            <h3>7.3 Trademarks</h3>
            <p>
              "Clarifyall" and our logo are trademarks of Clarifyall. You may not use our trademarks without prior written permission.
            </p>
          </section>

          <section className="legal-section">
            <h2>8. Privacy and Data Protection</h2>
            <p>
              Your use of our services is also governed by our Privacy Policy, which is incorporated into these Terms by reference. Please review our Privacy Policy to understand how we collect, use, and protect your personal information.
            </p>
          </section>

          <section className="legal-section">
            <h2>9. Disclaimers and Limitations of Liability</h2>
            
            <h3>9.1 Service "As Is"</h3>
            <p>
              OUR SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT.
            </p>

            <h3>9.2 No Warranty</h3>
            <p>We do not warrant that:</p>
            <ul>
              <li>Our services will be uninterrupted, secure, or error-free</li>
              <li>The information provided will be accurate or reliable</li>
              <li>Any defects will be corrected</li>
              <li>Our servers are free of viruses or harmful components</li>
            </ul>

            <h3>9.3 Limitation of Liability</h3>
            <p>
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, CLARIFYALL SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF PROFITS, DATA, USE, OR GOODWILL, ARISING OUT OF OR RELATED TO YOUR USE OF OUR SERVICES.
            </p>
            <p>
              OUR TOTAL LIABILITY SHALL NOT EXCEED THE AMOUNT YOU PAID TO US (IF ANY) IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM.
            </p>
          </section>

          <section className="legal-section">
            <h2>10. Indemnification</h2>
            <p>
              You agree to indemnify, defend, and hold harmless Clarifyall, its officers, directors, employees, and agents from any claims, liabilities, damages, losses, costs, or expenses (including reasonable attorneys' fees) arising out of or related to:
            </p>
            <ul>
              <li>Your use of our services</li>
              <li>Your violation of these Terms</li>
              <li>Your violation of any rights of another party</li>
              <li>Your content or submissions</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>11. Third-Party Links and Services</h2>
            <p>
              Our platform may contain links to third-party websites and services. We are not responsible for the content, privacy practices, or terms of service of these external sites. Your interactions with third-party sites are solely between you and the third party.
            </p>
          </section>

          <section className="legal-section">
            <h2>12. Dispute Resolution</h2>
            
            <h3>12.1 Governing Law</h3>
            <p>
              These Terms shall be governed by and construed in accordance with the laws of [Your Jurisdiction], without regard to its conflict of law provisions.
            </p>

            <h3>12.2 Arbitration</h3>
            <p>
              Any disputes arising out of or relating to these Terms or our services shall be resolved through binding arbitration, except that either party may seek injunctive relief in court for intellectual property infringement.
            </p>

            <h3>12.3 Class Action Waiver</h3>
            <p>
              You agree to resolve disputes on an individual basis and waive any right to participate in class action lawsuits or class-wide arbitration.
            </p>
          </section>

          <section className="legal-section">
            <h2>13. Termination</h2>
            <p>
              We may terminate or suspend your access to our services immediately, without prior notice or liability, for any reason, including breach of these Terms. Upon termination, your right to use our services will cease immediately.
            </p>
            <p>
              You may terminate your account at any time by contacting us or using the account deletion feature.
            </p>
          </section>

          <section className="legal-section">
            <h2>14. Changes to Terms</h2>
            <p>
              We reserve the right to modify these Terms at any time. We will notify users of material changes by posting the updated Terms on our website and updating the "Last Updated" date. Your continued use of our services after such changes constitutes acceptance of the modified Terms.
            </p>
          </section>

          <section className="legal-section">
            <h2>15. Severability</h2>
            <p>
              If any provision of these Terms is found to be unenforceable or invalid, that provision shall be limited or eliminated to the minimum extent necessary, and the remaining provisions shall remain in full force and effect.
            </p>
          </section>

          <section className="legal-section">
            <h2>16. Entire Agreement</h2>
            <p>
              These Terms, together with our Privacy Policy and any other legal notices published on our platform, constitute the entire agreement between you and Clarifyall regarding our services.
            </p>
          </section>

          <section className="legal-section">
            <h2>17. Contact Information</h2>
            <p>If you have any questions about these Terms of Service, please contact us:</p>
            <div className="contact-info">
              <p><strong>Email:</strong> legal@clarifyall.com</p>
              <p><strong>Address:</strong> Clarifyall Legal Department, [Your Address]</p>
            </div>
          </section>

          <section className="legal-section">
            <h2>18. Acknowledgment</h2>
            <p>
              BY USING CLARIFYALL, YOU ACKNOWLEDGE THAT YOU HAVE READ, UNDERSTOOD, AND AGREE TO BE BOUND BY THESE TERMS OF SERVICE.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}

export default TermsOfService;
